"use client";

import { Body, Icon, VStack } from "@jace/ui";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { MissingScopes } from "./missing-scopes";

export type AuthErrorType =
	| "ExchangeTokenMissing"
	| "missing-scopes"
	| "gmail-installation-failed"
	| "service-not-supported"
	| "user-removed";

export const AUTH_ERROR_MESSAGES: Record<string, string> = {
	ExchangeTokenMissing:
		"Exchange token is missing. We apologize for the inconvenience. Please try again.",
	"missing-scopes":
		"We need all required permissions to continue with the setup. Please accept them to proceed.",
	"gmail-installation-failed":
		"Account creation was unsuccessful. Please try again.",
	"service-not-supported":
		"We're sorry, this email provider is not currently supported.",
	"user-removed": "Your account has been removed. Please create a new account.",
};

export type ErrorSeverity = "error" | "warning" | "info";

export interface AuthErrorProps {
	className?: string;
	errorParam?: string; // Optional override for the error parameter name
	severity?: ErrorSeverity;
}

const severityStyles: Record<ErrorSeverity, string> = {
	error: "bg-red-500 text-white",
	warning: "bg-amber-500 text-white",
	info: "bg-blue-500 text-white",
};

const severityIcons: Record<ErrorSeverity, "error" | "warning" | "info"> = {
	error: "error",
	warning: "warning",
	info: "info",
};

export function AuthError({
	className = "",
	errorParam = "error",
	severity = "error",
}: AuthErrorProps) {
	const searchParams = useSearchParams();
	const [errors, setErrors] = useState<AuthErrorType[]>([]);

	useEffect(() => {
		const errorCode = searchParams.get(errorParam) as AuthErrorType | null;
		if (errorCode) {
			setErrors([errorCode]);
		} else {
			setErrors([]);
		}
	}, [searchParams, errorParam]);

	if (errors.length === 0) {
		return null;
	}

	return (
		<VStack className="gap-md w-full">
			{errors.map((error) => {
				const errorMessage =
					AUTH_ERROR_MESSAGES[error] ||
					"An unknown error occurred. Please try again.";

				return (
					<Body
						key={error}
						className={`rounded-lg px-lg py-md text-text-fineprint flex items-center gap-md !mb-0 text-left ${severityStyles[severity]} ${className}`}
					>
						<Icon kind={severityIcons[severity]} />
						{errorMessage}
					</Body>
				);
			})}
			{errors.includes("missing-scopes") ? <MissingScopes asModal /> : null}
		</VStack>
	);
}
